import axios, { AxiosRequestConfig } from "axios";

import {
  ADMIN_REFRESH_TOKEN,
  CUSTOMER_REFRESH_TOKEN,
} from "../../config/apiUrlsCustomer";

const apiClient = axios.create({
  withCredentials: true,
});

// ============================================================
// Refresh lock
//
// Prevent multiple API requests from refreshing the token
// simultaneously.
//
// Example:
//
// Request A -> 401 -> refresh
// Request B -> 401 -> waits for same refresh
// Request C -> 401 -> waits for same refresh
// ============================================================

const refreshPromises: Record<string, Promise<void> | null> = {};

// ============================================================
// Helpers
// ============================================================

function getApiPath(endPoint: string): string {
  try {
    return new URL(endPoint, window.location.origin).pathname.toLowerCase();
  } catch {
    return endPoint.toLowerCase();
  }
}

// ------------------------------------------------------------
// Login / authentication endpoints
// ------------------------------------------------------------

function isLoginEndpoint(endPoint: string): boolean {
  const path = getApiPath(endPoint);

  return (
    path.endsWith("/auth/guest-login") ||
    path.endsWith("/auth/send-otp") ||
    path.endsWith("/auth/verify-otp") ||
    path.endsWith("/auth/send-email-otp") ||
    path.endsWith("/auth/verify-email-otp") ||
    path.endsWith("/auth/admin-login")
  );
}

// ------------------------------------------------------------
// Refresh endpoints
// ------------------------------------------------------------

function isRefreshEndpoint(endPoint: string): boolean {
  const path = getApiPath(endPoint);

  return (
    path.includes("/auth/refresh-token") ||
    path.includes("/auth/admin-refresh-token")
  );
}

// ------------------------------------------------------------
// Determine which refresh endpoint to use
// ------------------------------------------------------------

function getRefreshUrl(endPoint: string): string | null {
  const path = getApiPath(endPoint);

  // ----------------------------------------------------------
  // Admin APIs
  // ----------------------------------------------------------

  if (path.startsWith("/api/admin")) {
    return ADMIN_REFRESH_TOKEN;
  }

  // ----------------------------------------------------------
  // Customer APIs
  //
  // Examples:
  // /api/cart
  // /api/addresses
  // /api/checkout
  // /api/products
  // /api/wallet
  // /api/customer/orders
  // /api/customer-booking/...
  // ----------------------------------------------------------

  if (path.startsWith("/api/")) {
    return CUSTOMER_REFRESH_TOKEN;
  }

  return null;
}

// ------------------------------------------------------------
// Session type helpers
// ------------------------------------------------------------

function isGuestCustomer(state: any): boolean {
  return (
    state?.auth?.customer?.isLoggedIn === true &&
    state?.auth?.customer?.customerType === "guest"
  );
}

function isRegisteredCustomer(state: any): boolean {
  return (
    state?.auth?.customer?.isLoggedIn === true &&
    state?.auth?.customer?.customerType === "registered"
  );
}

// ============================================================
// Main API Request
// ============================================================

export async function apiReq(
  endPoint: string,
  data: any,
  method: "get" | "post" | "put" | "patch" | "delete",
  headers: Record<string, string> = {},
  state: any,
  requestOptions: AxiosRequestConfig = {},
): Promise<any> {
  // ----------------------------------------------------------
  // Request configuration
  // ----------------------------------------------------------

  const config: AxiosRequestConfig = {
    headers,
    withCredentials: true,
    ...requestOptions,
  };

  // GET / DELETE parameters
  if (method === "get" || method === "delete") {
    config.params = data;
  }

  try {
    // ========================================================
    // First request
    // ========================================================

    const response =
      method === "get" || method === "delete"
        ? await apiClient[method](endPoint, config)
        : await apiClient[method](endPoint, data, config);

    return response;
  } catch (error: any) {
    const errorResponse = error?.response;

    // ========================================================
    // Endpoint classification
    // ========================================================

    const loginEndpoint = isLoginEndpoint(endPoint);

    const refreshEndpoint = isRefreshEndpoint(endPoint);

    // ========================================================
    // Handle 401
    // ========================================================

    if (errorResponse?.status === 401 && !loginEndpoint && !refreshEndpoint) {
      // ======================================================
      // GUEST CUSTOMER
      //
      // Guest users only have an access token.
      // They should NOT call customer refresh-token.
      // ======================================================

      if (isGuestCustomer(state)) {
        console.warn(
          "Guest session expired. " +
            "Customer refresh token will not be attempted.",
        );

        clearClientSession();
        window.dispatchEvent(new CustomEvent("customer-session-expired"));

        return Promise.reject({
          status: 401,
          data: {
            success: false,
            message: "Guest session expired.",
          },
        });
      }

      // ======================================================
      // Determine refresh URL
      // ======================================================

      const refreshUrl = getRefreshUrl(endPoint);

      // ------------------------------------------------------
      // Unknown API endpoint
      // ------------------------------------------------------

      if (!refreshUrl) {
        return Promise.reject({
          status: errorResponse?.status ?? 401,

          data: errorResponse?.data ?? {
            success: false,
            message: "Unauthorized.",
          },
        });
      }

      try {
        // ==========================================================
        // SINGLE REFRESH PER SESSION TYPE
        //
        // If another request is already refreshing the token,
        // wait for that refresh instead of starting another one.
        // ==========================================================

        if (!refreshPromises[refreshUrl]) {
          console.log(
            "Access token expired. Starting token refresh:",
            refreshUrl,
          );

          refreshPromises[refreshUrl] = apiClient
            .post(
              refreshUrl,
              {},
              {
                withCredentials: true,
              },
            )
            .then(() => {
              console.log("Token refresh successful:", refreshUrl);
            })
            .finally(() => {
              // Allow another refresh later when the new
              // access token eventually expires.
              refreshPromises[refreshUrl] = null;
            });
        } else {
          console.log(
            "Token refresh already in progress. Waiting:",
            refreshUrl,
          );
        }

        // ==========================================================
        // Wait for the existing/new refresh operation
        // ==========================================================

        await refreshPromises[refreshUrl];

        // ==========================================================
        // Retry original request
        //
        // Browser automatically sends the new HttpOnly
        // access-token cookie.
        // ==========================================================

        console.log("Retrying original request after refresh:", endPoint);

        const retryResponse =
          method === "get" || method === "delete"
            ? await apiClient[method](endPoint, config)
            : await apiClient[method](endPoint, data, config);

        return retryResponse;
      } catch (refreshError: any) {
        console.error("Refresh token failed:", refreshError);

        // Make sure the lock is released
        refreshPromises[refreshUrl] = null;

        clearClientSession();

        // ========================================================
        // Determine affected session
        // ========================================================

        const path = getApiPath(endPoint);

        const isAdminEndpoint = path.startsWith("/api/admin");

        const isCustomerEndpoint =
          path.startsWith("/api/") && !path.startsWith("/api/admin");

        // ========================================================
        // Admin session expired
        // ========================================================

        if (isAdminEndpoint) {
          window.dispatchEvent(new CustomEvent("admin-session-expired"));
        }

        // ========================================================
        // Customer session expired
        // ========================================================

        if (isCustomerEndpoint) {
          window.dispatchEvent(new CustomEvent("customer-session-expired"));
        }

        return Promise.reject({
          status: 401,
          data: {
            success: false,
            message: "Session expired.",
          },
        });
      }
    }

    // ========================================================
    // Return original API error
    // ========================================================

    return Promise.reject({
      status: errorResponse?.status ?? 500,

      data: errorResponse?.data ?? {
        success: false,
        message: "Something went wrong.",
      },
    });
  }
}

// ============================================================
// POST
// ============================================================

export function apiPost(
  endPoint: string,
  data: any,
  state: any,
  headers: Record<string, string> = {},
) {
  return apiReq(endPoint, data, "post", headers, state);
}

// ============================================================
// PUT
// ============================================================

export function apiPut(
  endPoint: string,
  data: any,
  state: any,
  headers: Record<string, string> = {},
) {
  return apiReq(endPoint, data, "put", headers, state);
}

// ============================================================
// PATCH
// ============================================================

export function apiPatch(
  endPoint: string,
  data: any,
  state: any,
  headers: Record<string, string> = {},
) {
  return apiReq(endPoint, data, "patch", headers, state);
}

// ============================================================
// DELETE
// ============================================================

export function apiDelete(
  endPoint: string,
  data: any,
  state: any,
  headers: Record<string, string> = {},
) {
  return apiReq(endPoint, data, "delete", headers, state);
}

// ============================================================
// GET
// ============================================================

export function apiGet(
  endPoint: string,
  data: any,
  state: any,
  headers: Record<string, string> = {},
  requestOptions: AxiosRequestConfig = {},
) {
  return apiReq(endPoint, data, "get", headers, state, requestOptions);
}

export function clearClientSession() {
  try {
    localStorage.clear();
  } catch (error) {
    console.error("Failed to clear localStorage:", error);
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.error("Failed to clear sessionStorage:", error);
  }

  // Clear non-HttpOnly cookies
  try {
    document.cookie.split(";").forEach((cookie) => {
      const cookieName = cookie.split("=")[0].trim();

      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  } catch (error) {
    console.error("Failed to clear cookies:", error);
  }
}
